import React, { useEffect } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import { RouteType } from './App.enums';
import { newGuid } from './Utils';

import Clients from './views/Clients/Clients';
import Dashboard from './views/Dashboard/Dashboard';
import Classifications from './views/Classifications/Classifications';
import Projects from './views/Projects/Projects';
import AccessControl from './views/AccessControl/AccessControl';
import Client from './views/Clients/Client/Client';
import Project from './views/Projects/Project/Project';
import Settings from './views/Settings/Settings';
import Reports from './views/Reports/Reports';
import ContentSearch from './views/ContentSearch/ContentSearch';
import ContentGovernance from './views/ContentGovernance/ContentGovernance';
import { User, UserRoleEnum } from './generated/graphql';
import { useCurrentUser, useIsExternalUser, usePreviewFeatures } from './App.hooks';
import { PreviewFeatureKey } from './previewFeatures';

export type AppRoute = {
    name: string,
    role?: UserRoleEnum,
    type: RouteType,
    icon: string,
    iconComponent?: React.ComponentType<any>,
    badge?: string,
    previewFeature?: PreviewFeatureKey,
    path: string,
    element: JSX.Element,
};

export const routes: AppRoute[] = [
    {
        name: "Dashboard",
        role: UserRoleEnum.None,
        icon: "k-i-thumbnails-up",
        type: RouteType.Main,
        path: "/dashboard",
        element: <Dashboard />
    },
    {
        name: "Content Search",
        role: UserRoleEnum.None,
        icon: "k-i-search",
        type: RouteType.Main,
        path: "/content-search",
        element: <ContentSearch />
    },
    {
        name: "Clients",
        role: UserRoleEnum.None,
        icon: "k-i-group",
        type: RouteType.Main,
        path: "/clients",
        element: <Clients />,
    },
    {
        name: "Client",
        role: UserRoleEnum.None,
        icon: "k-i-group",
        type: RouteType.None,
        path: "/clients/:clientId/*",
        element: <Client />,
    },
    {
        name: "Projects",
        role: UserRoleEnum.None,
        icon: "k-i-folder",
        type: RouteType.Main,
        path: "/projects",
        element: <Projects />,
    },
    {
        name: "Projects",
        role: UserRoleEnum.None,
        icon: "k-i-folder",
        type: RouteType.None,
        path: "/projects/:projectId/*",
        element: <Project />,
    },
    {
        name: "Classifications",
        role: UserRoleEnum.None,
        icon: "k-i-categorize",
        type: RouteType.Main,
        path: "/classifications",
        element: <Classifications />
    },
    {
        name: "Reports",
        role: UserRoleEnum.Admin,
        icon: "k-i-chart-bar",
        type: RouteType.Main,
        path: "/reports",
        element: <Reports />
    },
    {
        name: "Settings",
        role: UserRoleEnum.Admin,
        icon: "k-i-settings",
        type: RouteType.Main,
        path: "/settings",
        element: <Settings />
    },
    {
        name: "Access Control",
        role: UserRoleEnum.Admin,
        icon: "k-i-lock",
        type: RouteType.Main,
        path: "/AccessControl",
        element: <AccessControl />
    },
    {
        name: "Requests",
        role: UserRoleEnum.Admin,
        icon: "k-i-task",
        badge: "Preview",
        previewFeature: "content-governance",
        type: RouteType.Main,
        path: "/content-governance/*",
        element: <ContentGovernance />
    }
];

export const canAccessRoute = (
    route: AppRoute,
    currentUser: User | null,
    isExternalUser: boolean,
    hasFeature: (key: PreviewFeatureKey) => boolean,
) => {
    if (isExternalUser) return route.name === 'Projects';
    if (route.previewFeature) return currentUser?.role === UserRoleEnum.Admin || hasFeature(route.previewFeature);
    if (currentUser?.role === UserRoleEnum.Admin) return true;
    return route.role === UserRoleEnum.None;
};

function AppRouting() {
    const currentUser = useCurrentUser();
    const isExternalUser = useIsExternalUser();
    const { hasFeature, loading: previewFeaturesLoading } = usePreviewFeatures();

    if (previewFeaturesLoading) return null;

    return (
        <Routes>
            {
                routes.map(route => {
                    if (canAccessRoute(route, currentUser, isExternalUser, hasFeature))
                        return (<Route key={newGuid()} path={route.path} element={route.element} />);
                    return null;
                })
            }
            <Route path="/" element={<Navigate replace to={isExternalUser ? "/projects" : "/dashboard"} />} />
            <Route path="*" element={<Navigate replace to={isExternalUser ? "/projects" : "/dashboard"} />} />
        </Routes>
    );
}

export default AppRouting;